import { useEffect, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { styled } from '@mui/material/styles';
import Header, { GlobalAlerts } from './header';
import Nav from './nav';
import AuthGuard from '../../auth/AuthGuard';
import HelpWidget from '../../components/help-widget';
import ErrorBoundary from '../../components/error-boundary';
import useOnboardingStatus from 'src/hooks/useOnboardingStatus';
import { useAuthContext } from 'src/auth/useAuthContext';

const APP_BAR_MOBILE = 64;
const APP_BAR_DESKTOP = 92;

const StyledRoot = styled('div')({
  display: 'flex',
  minHeight: '100%',
  overflow: 'hidden',
});

const Main = styled('div')(({ theme }) => ({
  flexGrow: 1,
  overflow: 'auto',
  minHeight: '100%',
  paddingTop: APP_BAR_MOBILE + 24,
  paddingBottom: theme.spacing(10),
  [theme.breakpoints.up('lg')]: {
    paddingTop: APP_BAR_DESKTOP + 24,
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

export default function DashboardLayout() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const onboardingStatus = useOnboardingStatus();
  const { user } = useAuthContext();

  // Paths users can still access while onboarding is incomplete (account/billing/setup).
  const allowedDuringOnboarding = [
    '/dashboard/setup',
    '/dashboard/profile',
    
  ];

  useEffect(() => {
    if (onboardingStatus.loading) return;
    if (!onboardingStatus.hasSubscription) return; // let trial flow live on /dashboard/app
    if (onboardingStatus.complete) return;

    const isAllowed = allowedDuringOnboarding.some((path) =>
      pathname.startsWith(path)
    );

    if (!isAllowed) {
      navigate('/dashboard/setup', { replace: true });
    }
  }, [pathname, onboardingStatus, navigate]);

  const renderContent = () => (
    <StyledRoot>
      <Header onOpenNav={setOpen} />
      <Nav
        openNav={open}
        onCloseNav={() => setOpen(false)}
        onboardingLocked={!onboardingStatus.complete || !onboardingStatus.hasSubscription}
      />
      <Main>
        <GlobalAlerts />
        {/* Keyed by path, so a page error clears when the user moves to another page. */}
        <ErrorBoundary key={pathname}>
          <Outlet />
        </ErrorBoundary>
      </Main>
      
    </StyledRoot>
  );

  return <AuthGuard>{renderContent()}</AuthGuard>;
}
